import React from 'react';
import styled from 'styled-components';
import {
  StripeCardInput,
  StripeElementRef,
} from '../../atoms/stripe-inputs/stripe-card-input/stripe-card-input';
import { StripeExpiryInput } from '../../atoms/stripe-inputs/stripe-expiry-input/stripe-expiry-input.atom';
import { StripeCvcInput } from '../../atoms/stripe-inputs/stripe-cvc-input/stripe-cvc-input.atom';
import { useTranslation } from 'react-i18next';
import { FormDataShape } from '../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { documentMasks } from '../../../common/utils/document-masks';
import { Checkout } from '../../../common/types/checkout';
import * as PM from '../../organisms/modern/payment-form/payment-form.organism.style';
import InputMask from 'react-input-mask';

type Props = {
  checkout: Checkout;
  data: FormDataShape;
  onChange: (field: keyof FormDataShape, value: string) => void;
  numberRef: React.RefObject<StripeElementRef>;
  expiryRef: React.RefObject<StripeElementRef>;
  cvcRef: React.RefObject<StripeElementRef>;
  handleStripeChange: (
    field: 'number' | 'expiry' | 'cvc',
    s: { complete: boolean; error: string | null },
  ) => void;
};

const ModernStripeWrap = styled(PM.InputWrap)`
  label {
    padding: 0 6px;
    z-index: 2;
    pointer-events: none;
    background: #fff;
    transition: all 0.15s ease;
  }

  /* float label para inputs nativos */
  input:not(:placeholder-shown) + label,
  input:focus + label {
    top: 0;
    transform: translateY(-50%) scale(0.9);
    background: linear-gradient(to bottom, #eee 50%, #fff 50%);
  }

  /* campos Stripe: label sempre em cima */
  &.always-top-label label {
    top: 0;
    transform: translateY(-50%) scale(0.9);
    background: linear-gradient(to bottom, #eee 50%, #fff 50%);
  }

  .stripe-box {
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: var(--radius-sm);
    padding: 0 12px;
    border: 1px solid var(--border);
    transition: all 0.15s ease;

    /* tira qualquer borda que venha de dentro */
    > div {
      width: 100%;
      border: none !important;
      box-shadow: none !important;
      background: transparent;
    }
  }

  /* quando o iframe do Stripe está focado, estiliza a borda do wrapper */
  .stripe-box:has(.StripeElement--focus) {
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(40, 121, 254, 0.12);
  }

  input {
    width: 100%;
    height: 44px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    padding: 0 12px;
    background: #fff;
    font-size: 14px;
  }

  input:focus {
    outline: 0;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(40, 121, 254, 0.12);
  }
`;

const MaskedInput = styled(InputMask)`
  width: 100%;
  height: 44px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  padding: 0 12px;
  background: #fff;
  font-size: 14px;

  &:focus {
    outline: 0;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(40, 121, 254, 0.12);
  }
`;

export const StripeCardSectionModern: React.FC<Props> = ({
  data,
  onChange,
  numberRef,
  expiryRef,
  cvcRef,
  handleStripeChange,
  checkout,
}) => {
  const { t } = useTranslation();

  return (
    <PM.CardGrid>
      <ModernStripeWrap className="span-2 always-top-label">
        <div className="stripe-box">
          <StripeCardInput
            variant="modern"
            onStripeChange={handleStripeChange}
            ref={numberRef}
          />
        </div>
        <label htmlFor="stripe-card-number">{t('cardNumber')}</label>
      </ModernStripeWrap>

      <ModernStripeWrap className="span-2 always-top-label">
        <input
          id="stripe-card-name"
          type="text"
          value={data.cardName}
          onChange={(e) => onChange('cardName', e.target.value)}
        />
        <label htmlFor="stripe-card-name">{t('cardName')}</label>
      </ModernStripeWrap>

      <ModernStripeWrap className="always-top-label">
        <div className="stripe-box">
          <StripeExpiryInput
            variant="modern"
            onStripeChange={handleStripeChange}
            ref={expiryRef}
          />
        </div>
        <label htmlFor="stripe-expiry">{t('expiryDate')}</label>
      </ModernStripeWrap>

      <ModernStripeWrap className="always-top-label">
        <div className="stripe-box">
          <StripeCvcInput
            variant="modern"
            onStripeChange={handleStripeChange}
            ref={cvcRef}
          />
        </div>
        <label htmlFor="stripe-cvc">CVV</label>
      </ModernStripeWrap>
    </PM.CardGrid>
  );
};
